import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Clock, FileText, User, LogOut, Award, BookOpen, Settings, KeyRound, ChevronDown } from 'lucide-react';
import axios from 'axios';
import ChangePasswordModal from '../common/ChangePasswordModal';

const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tests');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const [testsResponse, resultsResponse] = await Promise.all([
        axios.get('/student/tests'),
        axios.get('/student/results')
      ]);
      setTests(testsResponse.data);
      setResults(resultsResponse.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'not_started': { label: 'Not Started', className: 'bg-gray-500' },
      'in_progress': { label: 'In Progress', className: 'bg-yellow-500' },
      'submitted': { label: 'Submitted', className: 'bg-blue-500' },
      'marked': { label: 'Marked', className: 'bg-green-500' },
      'published': { label: 'Published', className: 'bg-purple-500' }
    };
    
    const config = statusConfig[status] || { label: status, className: 'bg-gray-500' };
    return <Badge className={`${config.className} text-white`}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-main-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main-gradient">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-3">
              <img 
                src="/radiance-logo.webp" 
                alt="Radiance Education" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-2xl font-heading font-bold text-white">Student Portal</h1>
                <p className="text-blue-200">Welcome, {user.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Profile Menu */}
              <div className="relative">
                <Button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  variant="outline"
                  className="text-white border-white hover:bg-white hover:text-blue-600"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Profile
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
                
                {showProfileMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowProfileMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg z-20 py-2">
                      <button
                        onClick={() => {
                          setShowChangePassword(true);
                          setShowProfileMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-100 flex items-center text-slate-700"
                      >
                        <KeyRound className="w-4 h-4 mr-3 text-slate-500" />
                        Change Password
                      </button>
                    </div>
                  </>
                )}
              </div>
              
              <Button
                onClick={logout}
                variant="outline"
                className="text-white border-white hover:bg-white hover:text-blue-600"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={showChangePassword} 
        onClose={() => setShowChangePassword(false)} 
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="tab-navigation mb-8">
          <button
            className={`tab-button ${
              activeTab === 'tests' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('tests')}
            data-testid="tests-tab"
          >
            <BookOpen className="w-5 h-5 mr-2" />
            Available Tests
          </button>
          <button
            className={`tab-button ${
              activeTab === 'results' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('results')}
            data-testid="results-tab"
          >
            <Award className="w-5 h-5 mr-2" />
            Results & Feedback
          </button>
        </div>

        {/* Tests Tab */}
        {activeTab === 'tests' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-bold text-slate-700">
                Writing Tests - Partial Test
              </h2>
              <div className="text-sm text-slate-500">
                {tests.length} test(s) available
              </div>
            </div>

            {tests.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Tests Available</h3>
                  <p className="text-slate-500">
                    No tests have been assigned to you yet. Please check back later.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6">
                {tests.map((test) => (
                  <Card key={test.id} className="exam-card" data-testid={`test-card-${test.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl text-white mb-2">
                            {test.title}
                          </CardTitle>
                          <CardDescription className="text-slate-300">
                            IELTS Academic Writing • {test.duration_minutes} minutes
                          </CardDescription>
                        </div>
                        {getStatusBadge(test.submission_status)}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-slate-400">
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Duration: {test.duration_minutes} min
                          </div>
                          <div className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            Tasks: 1 & 2
                          </div>
                        </div>
                        
                        <div className="flex space-x-3">
                          {test.submission_status === 'not_started' && (
                            <Button
                              onClick={() => navigate(`/exam/pre-gate/${test.id}`)}
                              className="btn-primary"
                              data-testid={`start-test-${test.id}`}
                            >
                              Start Test
                            </Button>
                          )}
                          
                          {test.submission_status === 'in_progress' && (
                            <Button
                              onClick={() => navigate(`/exam/writing/${test.submission_id}`)}
                              className="btn-success"
                              data-testid={`resume-test-${test.id}`}
                            >
                              Resume Test
                            </Button>
                          )}
                          
                          {['submitted', 'marked', 'published'].includes(test.submission_status) && (
                            <Button
                              variant="outline"
                              className="text-slate-300 border-slate-600 hover:bg-slate-700"
                              disabled
                            >
                              {test.submission_status === 'submitted' ? 'Awaiting Review' : 
                               test.submission_status === 'marked' ? 'Under Review' : 'Completed'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-heading font-bold text-white">
                Results & Feedback
              </h2>
              <div className="text-sm text-slate-400">
                {results.length} result(s) published
              </div>
            </div>

            {results.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="text-center py-12">
                  <Award className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No Results Yet</h3>
                  <p className="text-slate-400">
                    Your test results will appear here once they are published by your instructor.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6">
                {results.map((result) => (
                  <Card key={result.submission_id} className="glass-card" data-testid={`result-card-${result.submission_id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl text-white mb-2">
                            {result.test_title}
                          </CardTitle>
                          <CardDescription className="text-slate-300">
                            Submitted: {formatDate(result.submitted_at)}
                          </CardDescription>
                        </div>
                        <div className="text-right">
                          <div className="band-score-card inline-block px-6 py-3">
                            <div className="text-sm font-medium">Final Band</div>
                            <div className="text-2xl font-bold">{result.final_band}</div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Task 1 Scores */}
                        <div className="criteria-card">
                          <h4 className="text-lg font-semibold text-white mb-3">Task 1 Scores</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-300">Task Achievement:</span>
                              <span className="text-white font-medium">{result.task1_scores.task_achievement}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-300">Coherence & Cohesion:</span>
                              <span className="text-white font-medium">{result.task1_scores.coherence_cohesion}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-300">Lexical Resource:</span>
                              <span className="text-white font-medium">{result.task1_scores.lexical_resource}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-300">Grammar & Accuracy:</span>
                              <span className="text-white font-medium">{result.task1_scores.grammar_accuracy}</span>
                            </div>
                            <div className="border-t border-slate-600 pt-2 mt-3">
                              <div className="flex justify-between font-semibold">
                                <span className="text-white">Task 1 Band:</span>
                                <span className="text-blue-400">{result.task1_scores.band_score}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Task 2 Scores */}
                        <div className="criteria-card">
                          <h4 className="text-lg font-semibold text-white mb-3">Task 2 Scores</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-300">Task Response:</span>
                              <span className="text-white font-medium">{result.task2_scores.task_achievement}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-300">Coherence & Cohesion:</span>
                              <span className="text-white font-medium">{result.task2_scores.coherence_cohesion}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-300">Lexical Resource:</span>
                              <span className="text-white font-medium">{result.task2_scores.lexical_resource}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-300">Grammar & Accuracy:</span>
                              <span className="text-white font-medium">{result.task2_scores.grammar_accuracy}</span>
                            </div>
                            <div className="border-t border-slate-600 pt-2 mt-3">
                              <div className="flex justify-between font-semibold">
                                <span className="text-white">Task 2 Band:</span>
                                <span className="text-blue-400">{result.task2_scores.band_score}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Instructor Summary */}
                      {result.instructor_summary && (
                        <div className="criteria-card mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3">Instructor Feedback</h4>
                          <p className="text-slate-300 leading-relaxed">{result.instructor_summary}</p>
                        </div>
                      )}

                      <div className="flex justify-between items-center">
                        <div className="text-sm text-slate-400">
                          <div>Word Count - Task 1: {result.word_count_task1} words</div>
                          <div>Word Count - Task 2: {result.word_count_task2} words</div>
                          <div>Published: {formatDate(result.published_at)}</div>
                        </div>
                        <Button
                          onClick={() => navigate('/student/results', { state: { selectedResult: result } })}
                          className="btn-primary"
                          data-testid={`view-detailed-feedback-${result.submission_id}`}
                        >
                          View Detailed Feedback
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentDashboard;